// Издаване на фактура: избери задължения (наем/сметки) за бърз старт,
// после редактирай редовете, номера и датата преди издаване.
// Данните на двете страни са автоматично от профилите.

import React, { useCallback, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, money } from '../../api';
import { showError, showSuccess } from '../../toast';
import { Button, Card, COLORS, Input, Label, Muted, Row, Screen, Title } from '../../ui';
import { t } from '../../i18n';

type Line = { name: string; qty: string; price: string };

// днешна дата като дд.мм.гггг
function todayBg(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

// „дд.мм.гггг“ → ISO дата (или null при невалиден вход)
function parseBgDate(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (d.getDate() !== Number(dd) || d.getMonth() !== Number(mm) - 1) return null;
  return d.toISOString();
}

export default function IssueInvoiceScreen({ route, navigation }: any) {
  const { tenancyId } = route.params;
  // Преиздаване („Редактирай"): формата идва попълнена от старата фактура;
  // при запис старата се анулира и се издава нова със следващ номер (чл. 114).
  const reissueOf = route.params.reissueOf as
    | { id: string; number: string; lines: Array<{ name: string; quantity: number; unitPriceCents: number }>; paymentMethod: string }
    | undefined;
  const [tenant, setTenant] = useState('');
  const [dues, setDues] = useState<any[]>([]);
  const [lines, setLines] = useState<Line[]>(
    reissueOf
      ? reissueOf.lines.map((l) => ({ name: l.name, qty: String(l.quantity), price: (l.unitPriceCents / 100).toFixed(2) }))
      : []
  );
  const [method, setMethod] = useState<'bank' | 'cash'>(reissueOf?.paymentMethod === 'cash' ? 'cash' : 'bank');
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(todayBg());
  const [loading, setLoading] = useState(false);

  useFocusEffect(
    useCallback(() => {
      api(`/invoices/dues/${tenancyId}`)
        .then((r) => {
          setTenant(r.tenant);
          setDues(r.dues);
        })
        .catch((e) => showError(e.message));
    }, [tenancyId])
  );

  const addDue = (d: any) =>
    setLines((ls) => [...ls, { name: d.name, qty: '1', price: (d.amountCents / 100).toFixed(2) }]);
  const addEmpty = () => setLines((ls) => [...ls, { name: '', qty: '1', price: '' }]);
  const setLine = (i: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i: number) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const parsed = lines
    .map((l) => ({
      name: l.name.trim(),
      quantity: Number(l.qty.replace(',', '.')) || 1,
      unitPriceCents: Math.round(Number(l.price.replace(',', '.')) * 100),
    }))
    .filter((l) => l.name && Number.isFinite(l.unitPriceCents) && l.unitPriceCents > 0);
  const total = parsed.reduce((s, l) => s + l.unitPriceCents * l.quantity, 0);

  const issue = async () => {
    const issueDate = parseBgDate(date);
    if (!issueDate) {
      showError(t('Невалидна дата. Използвайте формат дд.мм.гггг.'));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        paymentMethod: method,
        lines: parsed.map((l) => ({ name: l.name, quantity: l.quantity, unitPriceCents: l.unitPriceCents })),
      };
      const res = reissueOf
        ? await api(`/invoices/${reissueOf.id}/reissue`, { body: payload })
        : await api('/invoices', {
            body: { tenancyId, issueDate, ...(number.trim() ? { number: number.trim() } : {}), ...payload },
          });
      showSuccess(
        reissueOf
          ? t('Фактура № {old} е анулирана; издадена е нова № {next}.', { old: reissueOf.number, next: res.invoice.number })
          : t('Фактура № {number} е издадена.', { number: res.invoice.number })
      );
      navigation.replace('Invoices');
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen>
      <Title>{reissueOf ? t('Редакция на фактура') : t('Нова фактура')}</Title>
      {reissueOf && (
        <Muted>
          {t('По чл. 114 ЗДДС издадена фактура не се променя: № {number} ще бъде АНУЛИРАНА, а на нейно място ще се издаде нова със следващ номер и същите (или поправени) редове.', { number: reissueOf.number })}
        </Muted>
      )}
      <Muted>{t('Получател: {name}', { name: tenant })}</Muted>

      {dues.length > 0 && (
        <>
          <Label>{t('Бърз избор от задълженията')}</Label>
          {dues.map((d) => (
            <Pressable key={d.key} onPress={() => addDue(d)}>
              <Card style={{ paddingVertical: 12 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={{ color: COLORS.primary, fontWeight: '700', marginRight: 10, fontSize: 18 }}>＋</Text>
                  <Text style={{ flex: 1, color: COLORS.text }}>{d.name}</Text>
                  <Text style={{ fontWeight: '600', color: COLORS.text }}>{money(d.amountCents)}</Text>
                </View>
              </Card>
            </Pressable>
          ))}
        </>
      )}

      <Label>{t('Редове на фактурата')}</Label>
      {lines.length === 0 && (
        <Card>
          <Muted>{t('Няма добавени редове. Изберете от задълженията горе или добавете ръчно.')}</Muted>
        </Card>
      )}
      {lines.map((l, i) => (
        <Card key={i}>
          <Label>{t('Наименование')}</Label>
          <Input value={l.name} onChangeText={(t) => setLine(i, { name: t })} placeholder={t('Напр. Наем месец юли')} />
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <View style={{ flex: 1 }}>
              <Label>{t('Количество')}</Label>
              <Input value={l.qty} onChangeText={(t) => setLine(i, { qty: t })} keyboardType="decimal-pad" placeholder="1" />
            </View>
            <View style={{ flex: 1 }}>
              <Label>{t('Ед. цена (€)')}</Label>
              <Input value={l.price} onChangeText={(t) => setLine(i, { price: t })} keyboardType="decimal-pad" placeholder="0.00" />
            </View>
          </View>
          <Button title={t('Премахни реда')} variant="danger" onPress={() => removeLine(i)} />
        </Card>
      ))}
      <Button title={t('+ Добави ред ръчно')} variant="secondary" onPress={addEmpty} />

      <Card>
        <Label>{t('Номер на фактурата (по избор)')}</Label>
        <Input value={number} onChangeText={setNumber} keyboardType="number-pad" placeholder={t('автоматичен')} />
        <Muted>{t('Остави празно за автоматичен пореден номер. Ръчен номер продължава поредицата оттам.')}</Muted>

        <Label>{t('Дата на издаване')}</Label>
        <Input value={date} onChangeText={setDate} placeholder={t('дд.мм.гггг')} />

        <Label>{t('Начин на плащане')}</Label>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {(
            [
              ['bank', t('По банка')],
              ['cash', t('В брой')],
            ] as const
          ).map(([key, lbl]) => (
            <Pressable
              key={key}
              onPress={() => setMethod(key)}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 8,
                alignItems: 'center',
                backgroundColor: method === key ? COLORS.primary : COLORS.bg,
                borderWidth: 1,
                borderColor: method === key ? COLORS.primary : COLORS.border,
              }}
            >
              <Text style={{ color: method === key ? '#fff' : COLORS.text }}>{lbl}</Text>
            </Pressable>
          ))}
        </View>
        <Row left={t('Общо (данъчна основа)')} right={money(total)} />
        <Muted>{t('ДДС се начислява според настройката ви (Профил → Данни за фактури).')}</Muted>
      </Card>

      <Button
        title={t('Издай фактура ({amount})', { amount: money(total) })}
        onPress={issue}
        disabled={parsed.length === 0}
        loading={loading}
      />
    </Screen>
  );
}
